import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppStore } from "../../store/useAppStore";
import { ContextHeader } from "../../components/layout/ContextHeader";
import { StateTrack } from "../../components/ui/StateTrack";
import { Button } from "../../components/ui/Button";
import { JarvisPanel } from "../../components/situations/JarvisPanel";
import { stepStatus, tourMachine } from "../../domain/stateMachines";
import { assembledBrief } from "../../domain/jarvis";
import type { TourOutcomeKind } from "../../domain/types";

const outcomeOptions: Array<{ kind: TourOutcomeKind; label: string }> = [
  { kind: "interested-follow-up", label: "Interested / follow-up" },
  { kind: "needs-different-unit", label: "Needs different unit" },
  { kind: "not-a-fit", label: "Not a fit" },
  { kind: "closed-won", label: "Closed Won" }
];

export function TourFlow() {
  const { tourId } = useParams();
  const navigate = useNavigate();
  const tour = useAppStore((s) => s.tours.find((t) => t.id === tourId));
  const unit = useAppStore((s) => s.units.find((u) => u.id === tour?.unitId));
  const site = useAppStore((s) => s.sites.find((si) => si.id === tour?.siteId));
  const advanceTour = useAppStore((s) => s.advanceTour);
  const recordTourOutcome = useAppStore((s) => s.recordTourOutcome);
  const startMoveInFromTour = useAppStore((s) => s.startMoveInFromTour);
  const situations = useAppStore((s) => s.situations);
  const [objection, setObjection] = useState("");
  const [nextStep, setNextStep] = useState("");

  if (!tour || !unit) return <p className="p-6 text-[13.5px] text-muted-foreground">Tour not found.</p>;

  const situation = situations.find((s) => s.linkedTourId === tour.id);
  const steps = stepStatus(tourMachine, tour.status);

  return (
    <div className="flex flex-1 flex-col">
      <ContextHeader onBack crumb={`${site?.name.toUpperCase()} › ${unit.unitNumber}`} title={`Tour — ${tour.prospectCompany}`} subtitle={tour.useCase} />
      <div className="flex flex-1 flex-col gap-4 p-4">
        <div className="rounded-md border border-border bg-card p-3.5">
          <StateTrack steps={steps} />
        </div>

        <JarvisPanel>
          <p>{situation ? assembledBrief(situation) : `${tour.leadTemperature} lead via ${tour.source === "hubspot" ? "HubSpot" : "Google Calendar"}.`}</p>
        </JarvisPanel>

        <div className="rounded-md border border-border bg-card p-3.5">
          <Kv label="Contact" value={tour.prospectContact} />
          <Kv label="Use case" value={tour.useCase} />
          <Kv label="Budget" value={tour.budgetRange} />
          <Kv label="Source" value={tour.source === "hubspot" ? "HubSpot deal record" : "Google Calendar"} />
        </div>

        {tour.status === "scheduled" && <Button onClick={() => advanceTour(tour.id, "prep")}>Begin prep</Button>}

        {tour.status === "prep" && (
          <div className="flex flex-col gap-2">
            <p className="text-[13.5px] text-muted-foreground">Lead brief reviewed. Ready to start the tour?</p>
            <Button onClick={() => advanceTour(tour.id, "touring")}>Start tour</Button>
          </div>
        )}

        {tour.status === "touring" && (
          <div className="flex flex-col gap-2">
            <p className="text-[13.5px] text-muted-foreground">Capture the outcome once the walkthrough is finished.</p>
            <Button onClick={() => advanceTour(tour.id, "outcome-recorded")}>Record outcome</Button>
          </div>
        )}

        {tour.status === "outcome-recorded" && !tour.outcome && (
          <div className="flex flex-col gap-3 rounded-md border border-border bg-card p-3.5">
            <label className="block">
              <span className="mb-1 block font-mono text-[10.5px] uppercase tracking-wide text-muted-foreground">Main objection (optional)</span>
              <input value={objection} onChange={(e) => setObjection(e.target.value)} className="w-full rounded-sm border border-border p-2 text-[13.5px]" />
            </label>
            <label className="block">
              <span className="mb-1 block font-mono text-[10.5px] uppercase tracking-wide text-muted-foreground">Next step</span>
              <input value={nextStep} onChange={(e) => setNextStep(e.target.value)} className="w-full rounded-sm border border-border p-2 text-[13.5px]" />
            </label>
            <div className="grid grid-cols-2 gap-2">
              {outcomeOptions.map((opt) => (
                <Button key={opt.kind} variant={opt.kind === "closed-won" ? "primary" : "quiet"} onClick={() => recordTourOutcome(tour.id, opt.kind, objection || null, nextStep || "Follow up with prospect.")}>
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {tour.outcome && (
          <div className="rounded-md border border-border bg-card p-3.5">
            <p className="font-action text-[14px] font-bold text-foreground">Outcome: {tour.outcome.kind.replace(/-/g, " ")}</p>
            {tour.outcome.mainObjection && <p className="mt-1 text-[13px] text-muted-foreground">Objection: {tour.outcome.mainObjection}</p>}
            <p className="mt-1 text-[13px] text-muted-foreground">Next: {tour.outcome.nextStep}</p>
            {tour.status === "closed-won" && (
              <Button
                className="mt-3"
                onClick={() => {
                  startMoveInFromTour(tour.id, "2026-10-01");
                  navigate(`/units/${unit.id}`);
                }}
              >
                Start move-in readiness
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Kv({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className="text-[12.5px] text-muted-foreground">{label}</span>
      <span className="text-[13px] font-semibold text-foreground">{value}</span>
    </div>
  );
}
